import type {
  ImportBatch, ImportOptions, ImportRevertResult, ImportRow, ImportRowAction, ImportTemplateColumn,
  PriceList, Warehouse,
} from '@erp/shared';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronLeft, ChevronRight, Download, FileSpreadsheet, History, Loader2, Play, RotateCcw, Trash2, Undo2, Upload,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Card } from '../../components/shadcn/index.js';
import { Button, confirmDialog } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { formatNumber } from '../../lib/intl.js';
import { trimMoney } from '../../lib/money.js';

/** Rows fetched per preview page. */
const ROWS_PER_PAGE = 50;
/** Rows written per apply request. Small enough to keep each call quick. */
const APPLY_CHUNK = 100;
/**
 * Rows put back per revert request. Smaller than the apply chunk: undoing a row
 * can retire a product, and that walks seven line tables looking for a reason not
 * to.
 */
const REVERT_CHUNK = 50;

const ACTION_TONE: Record<ImportRowAction, string> = {
  create: 'bg-success/10 text-success',
  update: 'bg-brand/10 text-brand',
  skip_unit: 'bg-warning/10 text-warning',
  skip_duplicate: 'bg-warning/10 text-warning',
  error: 'bg-danger/10 text-danger',
};

/**
 * Spreadsheet catalogue import.
 *
 * A full page rather than a dialog, because the whole point is the middle step:
 * several thousand classified rows the owner scrolls through, with what is stored
 * now on the left and what the sheet says on the right, BEFORE anything is
 * written. Uploading only stages the file; nothing touches the catalogue until
 * "Apply" — and then in chunks, so the run can be watched and stopped.
 */
export function StockImportPage(): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const { t: tc } = useTranslation();
  const locale = i18n.language;

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [rowTotal, setRowTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState<ImportRowAction | 'all'>('all');
  const [changedOnly, setChangedOnly] = useState(false);
  const [rowSearch, setRowSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rowsLoading, setRowsLoading] = useState(false);

  const [applying, setApplying] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);

  // Past imports. Until this list existed there was no way to see that an import
  // had ever happened once the page was closed — which made "undo that one" a
  // question nobody could even ask.
  const [history, setHistory] = useState<ImportBatch[]>([]);
  /** Id of the batch currently being reverted, and how far it has got. */
  const [busyBatch, setBusyBatch] = useState<string | null>(null);
  const [revertDone, setRevertDone] = useState(0);

  // The column guide comes from the server so the page can never advertise a
  // header the parser has stopped accepting.
  const [templateColumns, setImportTemplateColumns] = useState<ImportTemplateColumn[]>([]);
  const [templateBusy, setTemplateBusy] = useState<'ar' | 'en' | null>(null);

  useEffect(() => {
    void (async () => {
      const [w, p, cols] = await Promise.all([
        api().get<{ items: Warehouse[] }>('/warehouses'),
        api().get<{ items: PriceList[] }>('/pricing/lists').catch(() => ({ data: { items: [] } })),
        // The guide is a nicety; an older API without it must not blank the page.
        api().get<{ items: ImportTemplateColumn[] }>('/inventory-import/template/columns')
          .catch(() => ({ data: { items: [] as ImportTemplateColumn[] } })),
      ]);
      const active = w.data.items.filter((x) => x.isActive);
      setWarehouses(active);
      setPriceLists(p.data.items);
      setImportTemplateColumns(cols.data.items);
      if (active.length === 0) return;
      setWarehouseId(active[0]!.id);
      if (active.length === 1) return;
      // Default to the warehouse the stock is actually kept in — by COUNT of
      // stock rows, not by which was touched last: a single stray adjustment in
      // an otherwise empty warehouse would win on recency. A shop can carry
      // several warehouses of which only one is real, and importing into an
      // empty one silently leaves the true stock untouched while building a
      // shadow set of levels beside it. One cheap head-count per warehouse.
      const counts = await Promise.all(
        active.map((wh) =>
          api()
            .get<{ total: number }>('/stock/levels', { params: { warehouseId: wh.id, limit: 1 } })
            .then((r) => ({ id: wh.id, total: r.data.total ?? 0 }))
            .catch(() => ({ id: wh.id, total: 0 })),
        ),
      );
      const busiest = counts.reduce((a, b) => (b.total > a.total ? b : a));
      if (busiest.total > 0) setWarehouseId(busiest.id);
    })();
  }, []);

  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedSearch(rowSearch), 300);
    return () => window.clearTimeout(h);
  }, [rowSearch]);

  const loadHistory = async (): Promise<void> => {
    // A nicety on an older API — never blank the page over it.
    const r = await api().get<{ items: ImportBatch[] }>('/inventory-import')
      .catch(() => ({ data: { items: [] as ImportBatch[] } }));
    setHistory(r.data.items);
  };

  useEffect(() => { void loadHistory(); }, []);

  useEffect(() => { setPage(0); }, [actionFilter, changedOnly, debouncedSearch]);

  const loadRows = async (b: ImportBatch): Promise<void> => {
    setRowsLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        limit: ROWS_PER_PAGE, offset: page * ROWS_PER_PAGE,
      };
      if (actionFilter !== 'all') params.action = actionFilter;
      if (changedOnly) params.changedOnly = true;
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      const r = await api().get<{ items: ImportRow[]; total: number }>(
        `/inventory-import/${b.id}/rows`, { params },
      );
      setRows(r.data.items);
      setRowTotal(r.data.total);
    } finally {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    if (batch) void loadRows(batch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch?.id, page, actionFilter, changedOnly, debouncedSearch]);

  /** Pull the generated workbook down as a file. Authenticated, so not a plain <a href>. */
  const onDownloadTemplate = async (lang: 'ar' | 'en'): Promise<void> => {
    setTemplateBusy(lang);
    try {
      const res = await api().get('/inventory-import/template', { params: { lang }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `catalogue-template-${lang}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('stock.import.templateFailed', { defaultValue: "Couldn't download the template" }));
    } finally {
      setTemplateBusy(null);
    }
  };

  const onUpload = async (): Promise<void> => {
    if (!file || !warehouseId) return;
    setUploading(true);
    try {
      const buf = await file.arrayBuffer();
      const r = await api().post<ImportBatch>('/inventory-import', buf, {
        params: { warehouseId, filename: file.name },
        headers: { 'Content-Type': 'application/octet-stream' },
      });
      setBatch(r.data);
      setPage(0);
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } })
        .response?.data?.cause?.code;
      toast.error(
        code === 'import.unreadable_file'
          ? t('stock.import.errors.unreadable', { defaultValue: "That file couldn't be read as a spreadsheet." })
          : code === 'import.empty_sheet'
            ? t('stock.import.errors.empty', { defaultValue: 'That sheet has no data rows.' })
            : tc('errors:server'),
      );
    } finally {
      setUploading(false);
    }
  };

  const saveOptions = async (next: Partial<ImportOptions>): Promise<void> => {
    if (!batch) return;
    const merged = { ...batch.options, ...next };
    // Optimistic: the toggles must feel instant, and the round-trip only
    // re-classifies (it writes nothing to the catalogue).
    setBatch({ ...batch, options: merged });
    try {
      const r = await api().patch<ImportBatch>(`/inventory-import/${batch.id}`, merged);
      setBatch(r.data);
    } catch {
      toast.error(tc('errors:server'));
      setBatch(batch);
    }
  };

  const runApply = async (): Promise<void> => {
    if (!batch) return;

    // Say what is about to happen, in counts, before anything is written — and
    // say it loudest about the one number that cannot be undone by re-importing:
    // how many products this run will CREATE. Only asked on the first chunk; a
    // resumed run has already been agreed to.
    if (batch.status === 'draft') {
      const s0 = batch.summary;
      const creating = batch.options.createMissing && s0.create > 0;
      const lines = [
        t('stock.import.confirm.update', { n: num(s0.update), defaultValue: 'Update {{n}} existing products.' }),
        creating
          ? t('stock.import.confirm.create', { n: num(s0.create), defaultValue: 'CREATE {{n}} new products — this sheet has barcodes your catalogue does not.' })
          : s0.create > 0
            ? t('stock.import.confirm.noCreate', { n: num(s0.create), defaultValue: 'Create nothing: {{n}} rows with an unknown barcode are skipped.' })
            : t('stock.import.confirm.noneMissing', { defaultValue: 'Create nothing — every row matched a product.' }),
        batch.options.updateStock
          ? t('stock.import.confirm.stock', { w: batch.warehouseName ?? '', defaultValue: 'Stock in {{w}} is SET to the sheet value, not added to it.' })
          : t('stock.import.confirm.noStock', { defaultValue: 'Stock is left alone.' }),
      ];
      const ok = await confirmDialog({
        title: t('stock.import.confirm.title', { defaultValue: 'Apply this import?' }),
        message: lines.join('\n'),
        tone: creating ? 'danger' : 'default',
        confirmLabel: t('stock.import.apply', { defaultValue: 'Apply to catalogue' }),
      });
      if (!ok) return;
    }

    setApplying(true);
    setStopRequested(false);
    let current = batch;
    try {
      for (;;) {
        const r = await api().post<{ done: boolean; summary: ImportBatch['summary'] }>(
          `/inventory-import/${batch.id}/apply`, { chunk: APPLY_CHUNK },
        );
        current = { ...current, summary: r.data.summary, status: r.data.done ? 'applied' : 'applying' };
        setBatch(current);
        if (r.data.done) break;
        // Checked between chunks, not mid-chunk: a chunk is already atomic per
        // row, so stopping here always leaves a clean boundary to resume from.
        if (stopRequested) break;
      }
      await loadRows(current);
      if (current.status === 'applied') {
        toast.success(t('stock.import.doneToast', { defaultValue: 'Import finished.' }));
      }
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setApplying(false);
    }
  };

  /**
   * Discard the batch on screen. Routed through the same handler the history
   * list uses, so an import that has already written rows is undone rather than
   * abandoned — the old wording here promised "anything already applied stays
   * applied", which was true only because nothing could put it back.
   */
  const onDiscard = async (): Promise<void> => {
    if (batch) await onDeleteBatch(batch);
  };

  const num = (n: number): string => formatNumber(n, { locale });

  /** Pull the original workbook back down. Authenticated, so not a plain <a href>. */
  const onDownloadFile = async (b: ImportBatch): Promise<void> => {
    try {
      const res = await api().get(`/inventory-import/${b.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('stock.import.fileFailed', { defaultValue: "Couldn't download that workbook" }));
    }
  };

  /**
   * Drive the chunked revert to completion, returning what it did.
   *
   * Throws on the first failed call rather than looping: a revert that cannot
   * make progress must stop where it is, with the rows it has not reached still
   * marked applied, so a second attempt picks up exactly there.
   */
  const drainRevert = async (b: ImportBatch): Promise<ImportRevertResult> => {
    let last: ImportRevertResult | null = null;
    let done = 0;
    for (;;) {
      const r = await api().post<ImportRevertResult>(
        `/inventory-import/${b.id}/revert`, { chunk: REVERT_CHUNK },
      );
      last = r.data;
      done += r.data.processed;
      setRevertDone(done);
      if (r.data.done) return last;
    }
  };

  /** Say what a finished revert actually did — including what it refused to touch. */
  const reportRevert = (r: ImportRevertResult): void => {
    const notes: string[] = [];
    if (r.itemsRetired > 0) {
      notes.push(t('stock.import.revert.retired', {
        n: num(r.itemsRetired), defaultValue: '{{n}} products retired',
      }));
    }
    if (r.outcomes.item_in_use > 0) {
      notes.push(t('stock.import.revert.inUse', {
        n: num(r.outcomes.item_in_use),
        defaultValue: '{{n}} kept — already sold or ordered',
      }));
    }
    if (r.outcomes.changed_since > 0) {
      notes.push(t('stock.import.revert.changedSince', {
        n: num(r.outcomes.changed_since),
        defaultValue: '{{n}} left alone — edited since the import',
      }));
    }
    if (r.outcomes.clamped_at_zero > 0) {
      notes.push(t('stock.import.revert.clamped', {
        n: num(r.outcomes.clamped_at_zero),
        defaultValue: '{{n}} stock rows landed on zero — the goods had sold',
      }));
    }
    if (r.outcomes.not_tracked > 0) {
      notes.push(t('stock.import.revert.notTracked', {
        n: num(r.outcomes.not_tracked),
        defaultValue: '{{n}} could not be undone — imported before undo was recorded',
      }));
    }
    const line = t('stock.import.revert.done', {
      n: num(r.outcomes.reverted + r.outcomes.clamped_at_zero),
      defaultValue: '{{n}} rows put back.',
    }) + (notes.length > 0 ? ` ${notes.join(' · ')}` : '');
    // A revert stops as soon as a whole chunk fails, so failures are the reason
    // the run ended early — not a footnote. They get the error toast.
    if (r.failed > 0) {
      toast.error(`${line} ${t('stock.import.revert.failed', {
        n: num(r.failed),
        defaultValue: '{{n}} could not be put back — the run stopped there.',
      })}`);
      return;
    }
    toast.success(line);
  };

  /**
   * Undo a past import's catalogue writes, keeping the record of it.
   *
   * The record is the point of keeping it: "this workbook, on this date, changed
   * these 4,858 rows and was then undone" is a more useful history than a gap.
   */
  const onRevert = async (b: ImportBatch): Promise<void> => {
    const ok = await confirmDialog({
      title: t('stock.import.revert.title', { defaultValue: 'Undo this import?' }),
      message: [
        t('stock.import.revert.body', {
          n: num(b.summary.applied),
          defaultValue: '{{n}} applied rows are put back: prices and stock this import wrote return to what they displaced, and products it created are retired.',
        }),
        t('stock.import.revert.safety', {
          defaultValue: 'Anything you changed yourself afterwards is left alone, and a product that has since sold is kept.',
        }),
        b.untrackedRows > 0
          ? t('stock.import.revert.untracked', {
            n: num(b.untrackedRows),
            defaultValue: '{{n}} rows were applied before undo was recorded — those can only have their new products retired, not their edits reversed.',
          })
          : '',
      ].filter(Boolean).join('\n'),
      tone: 'danger',
      confirmLabel: t('stock.import.revert.confirm', { defaultValue: 'Undo it' }),
    });
    if (!ok) return;

    setBusyBatch(b.id);
    setRevertDone(0);
    try {
      reportRevert(await drainRevert(b));
      await loadHistory();
      if (batch?.id === b.id) setBatch((await api().get<ImportBatch>(`/inventory-import/${b.id}`)).data);
    } catch {
      toast.error(tc('errors:server'));
      await loadHistory();
    } finally {
      setBusyBatch(null);
    }
  };

  /**
   * Remove a past import — and, by default, the changes it made.
   *
   * Deleting takes the undo record with it (it lives on the staging rows), so
   * this is the last moment those changes can be put back. The confirmation says
   * so, and the server refuses a delete that would strand them unless the caller
   * explicitly asks to keep them.
   */
  const onDeleteBatch = async (b: ImportBatch): Promise<void> => {
    const hasChanges = b.canRevert;
    const ok = await confirmDialog({
      title: t('stock.import.remove.title', { defaultValue: 'Delete this import?' }),
      message: hasChanges
        ? [
          t('stock.import.remove.withRevert', {
            n: num(b.summary.applied),
            defaultValue: 'Its {{n}} applied rows are undone first, then the record and the stored workbook are deleted.',
          }),
          t('stock.import.revert.safety', {
            defaultValue: 'Anything you changed yourself afterwards is left alone, and a product that has since sold is kept.',
          }),
        ].join('\n')
        : t('stock.import.remove.recordOnly', {
          defaultValue: 'The record and the stored workbook are deleted. Nothing in the catalogue changes.',
        }),
      tone: 'danger',
      confirmLabel: hasChanges
        ? t('stock.import.remove.confirmRevert', { defaultValue: 'Undo and delete' })
        : t('stock.import.remove.confirm', { defaultValue: 'Delete' }),
    });
    if (!ok) return;

    setBusyBatch(b.id);
    setRevertDone(0);
    try {
      if (hasChanges) reportRevert(await drainRevert(b));
      await api().delete(`/inventory-import/${b.id}`);
      if (batch?.id === b.id) { setBatch(null); setRows([]); setFile(null); }
      await loadHistory();
    } catch {
      toast.error(tc('errors:server'));
      await loadHistory();
    } finally {
      setBusyBatch(null);
    }
  };

  const lastPage = Math.max(0, Math.ceil(rowTotal / ROWS_PER_PAGE) - 1);

  const header = (
    <header>
      <Link
        to="/inventory/stock"
        className="mb-1 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />
        {t('stock.title')}
      </Link>
      <h1 className="text-2xl font-semibold">
        {t('stock.import.title', { defaultValue: 'Import from Excel' })}
      </h1>
      <p className="mt-1 text-sm text-fg-muted">
        {t('stock.import.description', {
          defaultValue:
            'Upload a price/stock workbook. Products are matched by barcode and updated in place — never duplicated. Nothing is written until you apply.',
        })}
      </p>
    </header>
  );

  // ── step 1: upload ───────────────────────────────────────────────────────
  if (!batch) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <Card className="flex flex-col gap-4 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">{t('stock.adjust.warehouse')}</span>
              <select
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                className="h-10 rounded-md border border-border bg-bg px-3 text-sm"
              >
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              <span className="text-xs text-fg-muted">
                {t('stock.import.warehouseHint', {
                  defaultValue: 'Every quantity in the sheet is set in this warehouse.',
                })}
              </span>
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium">{t('stock.import.file', { defaultValue: 'Spreadsheet' })}</span>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="h-10 rounded-md border border-border bg-bg px-3 py-2 text-sm file:me-3 file:rounded file:border-0 file:bg-surface-sunk file:px-2 file:py-1 file:text-xs"
              />
              <span className="text-xs text-fg-muted">
                {t('stock.import.fileHint', {
                  defaultValue:
                    'Columns are found by their headers (Arabic or English): barcode, name, unit, retail / market / stationery price, last purchase cost, quantity.',
                })}
              </span>
            </label>
          </div>
          <div>
            <Button disabled={!file || !warehouseId || uploading} onClick={() => void onUpload()}>
              {uploading
                ? <Loader2 className="me-1.5 h-4 w-4 animate-spin" aria-hidden />
                : <Upload className="me-1.5 h-4 w-4" aria-hidden />}
              {t('stock.import.upload', { defaultValue: 'Upload and preview' })}
            </Button>
          </div>
        </Card>

        {/* Don't have a workbook? Start from one that is guaranteed to parse. */}
        <Card className="flex flex-col gap-4 p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <FileSpreadsheet className="h-4 w-4 text-fg-muted" aria-hidden />
                {t('stock.import.template', { defaultValue: 'Template' })}
              </h2>
              <p className="mt-1 text-sm text-fg-muted">
                {t('stock.import.templateHint', {
                  defaultValue:
                    'An empty workbook with the headers this importer reads. Sample rows sit on a second sheet, so they are never imported.',
                })}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={templateBusy !== null} onClick={() => void onDownloadTemplate('ar')}>
                {templateBusy === 'ar'
                  ? <Loader2 className="me-1.5 h-4 w-4 animate-spin" aria-hidden />
                  : <Download className="me-1.5 h-4 w-4" aria-hidden />}
                {t('stock.import.templateAr', { defaultValue: 'Arabic headers' })}
              </Button>
              <Button variant="secondary" disabled={templateBusy !== null} onClick={() => void onDownloadTemplate('en')}>
                {templateBusy === 'en'
                  ? <Loader2 className="me-1.5 h-4 w-4 animate-spin" aria-hidden />
                  : <Download className="me-1.5 h-4 w-4" aria-hidden />}
                {t('stock.import.templateEn', { defaultValue: 'English headers' })}
              </Button>
            </div>
          </div>

          {templateColumns.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-start text-xs uppercase text-fg-muted">
                    <th className="py-2 pe-3 text-start font-medium">{t('stock.import.templateColumn', { defaultValue: 'Column' })}</th>
                    <th className="py-2 pe-3 text-start font-medium">{t('stock.import.templateHeaderAr', { defaultValue: 'Arabic header' })}</th>
                    <th className="py-2 pe-3 text-start font-medium">{t('stock.import.templateHeaderEn', { defaultValue: 'English header' })}</th>
                    <th className="py-2 text-start font-medium">{t('stock.import.templateExample', { defaultValue: 'Example' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {templateColumns.map((c) => (
                    <tr key={c.key} className="border-b border-border/60 last:border-0">
                      <td className="py-2 pe-3">
                        {t(`stock.import.col.${c.key}`, { defaultValue: c.en })}
                        {c.key === 'barcode' && (
                          <span className="ms-1.5 rounded bg-danger/10 px-1.5 py-0.5 text-[11px] text-danger">
                            {t('stock.import.required', { defaultValue: 'required' })}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pe-3 text-fg-muted" dir="rtl">{c.ar}</td>
                      <td className="py-2 pe-3 text-fg-muted">{c.en}</td>
                      <td className="py-2 font-mono text-xs text-fg-muted" dir="auto">{c.sample}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-fg-muted">
                {t('stock.import.templateFooter', {
                  defaultValue:
                    'Column order does not matter — headers are matched wherever they sit, in either script. A column you leave out is simply left untouched in the ERP.',
                })}
              </p>
            </div>
          )}
        </Card>

        {/*
          Past imports.

          The list exists so an import can be UNDONE — which needs the run to
          still be findable weeks later, and needs the workbook that caused it to
          still be here. Before this, an import left a stock adjustment trail and
          nothing else: on 2026-08-05 a sheet created 2,660 products and working
          out which ones took a database backup and hand-written SQL.
        */}
        {history.length > 0 && (
          <Card className="flex flex-col gap-4 p-6">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <History className="h-4 w-4 text-fg-muted" aria-hidden />
                {t('stock.import.history.title', { defaultValue: 'Past imports' })}
              </h2>
              <p className="mt-1 text-sm text-fg-muted">
                {t('stock.import.history.hint', {
                  defaultValue:
                    'Every workbook is kept with the run it caused. Undoing one puts back the prices and stock it wrote and retires the products it created — leaving alone anything you have changed since.',
                })}
              </p>
            </div>
            <ul className="flex flex-col divide-y divide-border/60">
              {history.map((b) => (
                <ImportHistoryRow
                  key={b.id}
                  batch={b}
                  busy={busyBatch === b.id}
                  revertDone={busyBatch === b.id ? revertDone : 0}
                  anyBusy={busyBatch !== null}
                  num={num}
                  locale={locale}
                  onOpen={() => { setBatch(b); setPage(0); }}
                  onDownload={() => void onDownloadFile(b)}
                  onRevert={() => void onRevert(b)}
                  onDelete={() => void onDeleteBatch(b)}
                />
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  // ── step 2+3: preview, options, apply ────────────────────────────────────
  const s = batch.summary;
  const isDraft = batch.status === 'draft';
  const progressed = s.applied + s.skipped + s.failed;
  const percent = s.total === 0 ? 0 : Math.round((progressed / s.total) * 100);

  const chip = (
    key: ImportRowAction | 'all',
    label: string,
    count: number,
    tone: string,
  ): JSX.Element => (
    <button
      key={key}
      type="button"
      aria-pressed={actionFilter === key}
      onClick={() => setActionFilter(key)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition',
        actionFilter === key ? 'border-brand bg-brand/10 text-brand' : 'border-border text-fg-muted hover:text-fg',
      )}
    >
      <span className={cn('rounded-full px-1.5 py-0.5 tabular-nums', tone)}>{num(count)}</span>
      {label}
    </button>
  );

  const toggle = (
    id: keyof ImportOptions,
    label: string,
    hint?: string,
  ): JSX.Element => (
    <label className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={Boolean(batch.options[id])}
        disabled={!isDraft}
        onChange={(e) => void saveOptions({ [id]: e.target.checked } as Partial<ImportOptions>)}
      />
      <span>
        <span className="font-medium">{label}</span>
        {hint ? <span className="block text-xs text-fg-muted">{hint}</span> : null}
      </span>
    </label>
  );

  return (
    <div className="flex flex-col gap-4">
      {header}

      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="h-4 w-4 text-fg-muted" aria-hidden />
            <span className="font-medium">{batch.filename}</span>
            <span className="text-fg-muted">
              · {num(batch.rowCount)} {t('stock.import.rows', { defaultValue: 'rows' })}
              · {batch.warehouseName}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => void onDiscard()} disabled={applying}>
              <Trash2 className="me-1.5 h-4 w-4" aria-hidden />
              {t('stock.import.discard', { defaultValue: 'Discard this import' })}
            </Button>
            {batch.status !== 'applied' && (
              <Button onClick={() => void runApply()} disabled={applying}>
                {applying
                  ? <Loader2 className="me-1.5 h-4 w-4 animate-spin" aria-hidden />
                  : <Play className="me-1.5 h-4 w-4" aria-hidden />}
                {applying
                  ? t('stock.import.applying', { defaultValue: 'Applying…' })
                  : t('stock.import.apply', { defaultValue: 'Apply to catalogue' })}
              </Button>
            )}
            {applying && (
              <Button variant="secondary" onClick={() => setStopRequested(true)}>
                {t('stock.import.stop', { defaultValue: 'Stop after this chunk' })}
              </Button>
            )}
          </div>
        </div>

        {/* The one number that can silently double a catalogue. Said out loud,
            both ways round, so neither outcome is a surprise after the fact. */}
        {isDraft && s.create > 0 && (
          <div
            className={cn(
              'flex items-start gap-2 rounded-md border p-3 text-sm',
              batch.options.createMissing
                ? 'border-warning/40 bg-warning/10 text-warning'
                : 'border-border bg-surface-sunk text-fg-muted',
            )}
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {batch.options.createMissing
                ? t('stock.import.createWarn', {
                    n: num(s.create),
                    defaultValue: '{{n}} rows carry a barcode no product in this catalogue has. They will be ADDED as new products. If this sheet describes stock you already sell, that creates a second copy of each — turn "Create missing products" off to update only.',
                  })
                : t('stock.import.createSkip', {
                    n: num(s.create),
                    defaultValue: '{{n}} rows carry a barcode no product in this catalogue has. They will be skipped — nothing is created. Only prices, stock and cost of products already in the catalogue change.',
                  })}
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {chip('all', t('stock.import.action.all', { defaultValue: 'All rows' }), s.total, 'bg-surface-sunk text-fg')}
          {chip('update', t('stock.import.action.update', { defaultValue: 'Update' }), s.update, ACTION_TONE.update)}
          {chip(
            'create',
            batch.options.createMissing
              ? t('stock.import.action.create', { defaultValue: 'New product' })
              : t('stock.import.action.createOff', { defaultValue: 'Unknown barcode — skipped' }),
            s.create,
            batch.options.createMissing ? ACTION_TONE.create : ACTION_TONE.skip_duplicate,
          )}
          {chip('skip_unit', t('stock.import.action.skipUnit', { defaultValue: 'Carton barcode' }), s.skipUnit, ACTION_TONE.skip_unit)}
          {chip('skip_duplicate', t('stock.import.action.skipDuplicate', { defaultValue: 'Duplicate' }), s.skipDuplicate, ACTION_TONE.skip_duplicate)}
          {chip('error', t('stock.import.action.error', { defaultValue: 'Problem' }), s.error, ACTION_TONE.error)}
        </div>

        {!isDraft && (
          <div className="flex flex-col gap-1.5">
            <div className="h-2 overflow-hidden rounded-full bg-surface-sunk">
              <div className="h-full bg-brand transition-all" style={{ width: `${percent}%` }} />
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-fg-muted">
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
                {t('stock.import.appliedCount', { defaultValue: 'Applied' })}: {num(s.applied)}
              </span>
              <span>{t('stock.import.skippedCount', { defaultValue: 'Skipped' })}: {num(s.skipped)}</span>
              <span className={cn(s.failed > 0 && 'text-danger')}>
                {t('stock.import.failedCount', { defaultValue: 'Failed' })}: {num(s.failed)}
              </span>
              <span>{t('stock.import.pendingCount', { defaultValue: 'Remaining' })}: {num(s.pending)}</span>
            </div>
          </div>
        )}
      </Card>

      {/* What to write. Every column is a separate decision — a sheet can be
          right about prices and wrong about names, and declining one column
          must not mean declining the import. */}
      <Card className="flex flex-col gap-3 p-5">
        <h2 className="text-sm font-semibold">
          {t('stock.import.whatToApply', { defaultValue: 'What to apply' })}
          {!isDraft && (
            <span className="ms-2 text-xs font-normal text-fg-muted">
              {t('stock.import.lockedOnce', { defaultValue: 'Locked once the import starts.' })}
            </span>
          )}
        </h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {toggle('updatePrices', t('stock.import.opt.prices', { defaultValue: 'Prices' }),
            t('stock.import.opt.pricesHint', { defaultValue: 'Retail price and the customer-group price lists.' }))}
          {toggle('updateStock', t('stock.import.opt.stock', { defaultValue: 'Stock quantities' }),
            t('stock.import.opt.stockHint', { defaultValue: 'On hand is SET to the sheet value, not added to it.' }))}
          {toggle('updateCost', t('stock.import.opt.cost', { defaultValue: 'Cost' }),
            t('stock.import.opt.costHint', { defaultValue: 'Replaces the average cost — affects valuation, not the ledger.' }))}
          {toggle('updateNames', t('stock.import.opt.names', { defaultValue: 'Product names' }))}
          {toggle('updateUnits', t('stock.import.opt.units', { defaultValue: 'Units' }))}
          {toggle('createMissing', t('stock.import.opt.create', { defaultValue: 'Create missing products' }),
            t('stock.import.opt.createHint', {
              defaultValue: 'OFF: rows with an unknown barcode are skipped, and the import only updates. ON: each becomes a NEW product — a barcode this catalogue does not carry looks identical to a product you already sell under a different code.',
            }))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">{t('stock.import.map.market', { defaultValue: 'Market price column →' })}</span>
            <select
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm"
              value={batch.options.marketPriceListId ?? ''}
              disabled={!isDraft}
              onChange={(e) => void saveOptions({ marketPriceListId: e.target.value || null })}
            >
              <option value="">{t('stock.import.map.none', { defaultValue: '— Don’t import —' })}</option>
              {priceLists.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.currencyCode ?? '—'})</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">{t('stock.import.map.stationery', { defaultValue: 'Stationery price column →' })}</span>
            <select
              className="h-9 rounded-md border border-border bg-bg px-2 text-sm"
              value={batch.options.stationeryPriceListId ?? ''}
              disabled={!isDraft}
              onChange={(e) => void saveOptions({ stationeryPriceListId: e.target.value || null })}
            >
              <option value="">{t('stock.import.map.none', { defaultValue: '— Don’t import —' })}</option>
              {priceLists.map((p) => (
                <option key={p.id} value={p.id}>{p.name} ({p.currencyCode ?? '—'})</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">{t('stock.import.map.retailCurrency', { defaultValue: 'Retail price currency' })}</span>
            <input
              className="h-9 rounded-md border border-border bg-bg px-2 font-mono text-sm uppercase"
              maxLength={3}
              value={batch.options.retailCurrencyCode ?? ''}
              disabled={!isDraft}
              onChange={(e) => void saveOptions({ retailCurrencyCode: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">{t('stock.import.map.costCurrency', { defaultValue: 'Cost currency' })}</span>
            <input
              className="h-9 rounded-md border border-border bg-bg px-2 font-mono text-sm uppercase"
              maxLength={3}
              value={batch.options.costCurrencyCode ?? ''}
              disabled={!isDraft}
              onChange={(e) => void saveOptions({ costCurrencyCode: e.target.value.toUpperCase() })}
            />
          </label>
        </div>
      </Card>

      {/* Preview */}
      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <input
            type="search"
            value={rowSearch}
            onChange={(e) => setRowSearch(e.target.value)}
            placeholder={t('stock.import.searchRows', { defaultValue: 'Search barcode or name…' })}
            aria-label={t('stock.import.searchRows', { defaultValue: 'Search barcode or name…' })}
            className="h-8 w-64 rounded-full border border-border bg-bg px-3.5 text-xs outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
          <label className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
            <input type="checkbox" checked={changedOnly} onChange={(e) => setChangedOnly(e.target.checked)} />
            {t('stock.import.changedOnly', { defaultValue: 'Only rows that change something' })}
          </label>
          {rowsLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-fg-muted" aria-hidden />}
        </div>

        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-fg-muted">
            {t('stock.import.noRows', { defaultValue: 'No rows match this filter.' })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-xs uppercase text-fg-muted">
                <tr>
                  <th className="px-3 py-2 text-start">#</th>
                  <th className="px-3 py-2 text-start">{t('stock.columns.barcode', { defaultValue: 'Barcode' })}</th>
                  <th className="px-3 py-2 text-start">{t('stock.import.col.what', { defaultValue: 'What happens' })}</th>
                  <th className="px-3 py-2 text-start">{t('stock.import.col.changes', { defaultValue: 'Changes' })}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 align-top last:border-0">
                    <td className="px-3 py-2 font-mono text-xs text-fg-subtle">{r.rowNumber}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.barcode || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={cn('inline-block rounded-full px-2 py-0.5 text-xs font-semibold', ACTION_TONE[r.action])}>
                        {t(`stock.import.action.${r.action === 'skip_unit' ? 'skipUnit' : r.action === 'skip_duplicate' ? 'skipDuplicate' : r.action}`, {
                          defaultValue: r.action,
                        })}
                      </span>
                      {r.message && (
                        <span className="ms-2 inline-flex items-center gap-1 text-xs text-fg-muted">
                          <AlertTriangle className="h-3 w-3" aria-hidden />
                          {t(`stock.import.reason.${r.message}`, { defaultValue: r.message })}
                          {r.before?.ownerItemName ? ` — ${r.before.ownerItemName}` : ''}
                        </span>
                      )}
                      {r.status !== 'pending' && (
                        <span className={cn(
                          'ms-2 text-xs',
                          r.status === 'failed' ? 'text-danger' : 'text-fg-subtle',
                        )}>
                          {t(`stock.import.status.${r.status}`, { defaultValue: r.status })}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <RowDiff row={r} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-xs text-fg-muted">
          <span>
            {t('stock.showing', {
              defaultValue: 'Showing {{from}}–{{to}} of {{total}}',
              from: num(rowTotal === 0 ? 0 : page * ROWS_PER_PAGE + 1),
              to: num(Math.min(rowTotal, page * ROWS_PER_PAGE + rows.length)),
              total: num(rowTotal),
            })}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
            </Button>
            <span className="px-1 tabular-nums">{page + 1} / {lastPage + 1}</span>
            <Button variant="ghost" size="sm" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))}>
              <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
            </Button>
          </div>
        </div>
      </Card>

      {batch.status === 'applied' && (
        <Card className="flex items-center justify-between gap-3 p-5">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />
            {t('stock.import.finished', {
              defaultValue: '{{applied}} rows applied, {{skipped}} skipped, {{failed}} failed.',
              applied: num(s.applied), skipped: num(s.skipped), failed: num(s.failed),
            })}
          </div>
          <Button variant="secondary" onClick={() => { setBatch(null); setFile(null); setRows([]); }}>
            <RotateCcw className="me-1.5 h-4 w-4" aria-hidden />
            {t('stock.import.another', { defaultValue: 'Import another file' })}
          </Button>
        </Card>
      )}
    </div>
  );
}

/** Tone for each terminal state a past import can be sitting in. */
const BATCH_TONE: Record<string, string> = {
  draft: 'bg-warning/10 text-warning',
  applying: 'bg-brand/10 text-brand',
  reverting: 'bg-brand/10 text-brand',
  applied: 'bg-success/10 text-success',
  reverted: 'bg-fg-muted/10 text-fg-muted',
  cancelled: 'bg-fg-muted/10 text-fg-muted',
};

/**
 * One past import: what it was, what it did, and the three things you can do to
 * it — open it again, take the workbook back, or put the catalogue back.
 */
function ImportHistoryRow({
  batch, busy, revertDone, anyBusy, num, locale, onOpen, onDownload, onRevert, onDelete,
}: {
  batch: ImportBatch;
  busy: boolean;
  revertDone: number;
  anyBusy: boolean;
  num: (n: number) => string;
  locale: string;
  onOpen: () => void;
  onDownload: () => void;
  onRevert: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { t } = useTranslation('inventory');
  const s = batch.summary;
  const when = new Date(batch.createdAt).toLocaleDateString(locale, {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  return (
    <li className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {/* dir=auto: an Arabic filename beside a Latin one must each read the
              way it was written. */}
          <button
            type="button"
            onClick={onOpen}
            dir="auto"
            className="truncate text-sm font-medium hover:text-brand hover:underline"
          >
            {batch.filename}
          </button>
          <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', BATCH_TONE[batch.status] ?? '')}>
            {t(`stock.import.status.${batch.status}`, { defaultValue: batch.status })}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-fg-muted">
          {when}
          {batch.warehouseName ? ` · ${batch.warehouseName}` : ''}
          {' · '}
          {t('stock.import.history.counts', {
            defaultValue: '{{rows}} rows · {{applied}} applied · {{reverted}} undone',
            rows: num(batch.rowCount), applied: num(s.applied), reverted: num(s.reverted),
          })}
          {batch.untrackedRows > 0 && (
            <> · <span className="text-warning">
              {t('stock.import.history.untracked', {
                n: num(batch.untrackedRows), defaultValue: '{{n}} not undoable',
              })}
            </span></>
          )}
        </p>
        {busy && (
          <p className="mt-1 text-xs text-brand">
            <Loader2 className="me-1 inline h-3 w-3 animate-spin" aria-hidden />
            {t('stock.import.revert.progress', {
              done: num(revertDone), total: num(s.applied + s.reverted),
              defaultValue: 'Putting back {{done}} of {{total}}…',
            })}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {/* Only offered when the file was actually kept — batches imported before
            the workbook was stored have nothing to hand back. */}
        {batch.file && (
          <Button variant="ghost" size="sm" disabled={anyBusy} onClick={onDownload}
            title={t('stock.import.history.download', { defaultValue: 'Download the workbook' })}>
            <Download className="h-4 w-4" aria-hidden />
          </Button>
        )}
        {batch.canRevert && (
          <Button variant="ghost" size="sm" disabled={anyBusy} onClick={onRevert}>
            <Undo2 className="me-1.5 h-4 w-4" aria-hidden />
            {t('stock.import.revert.action', { defaultValue: 'Undo' })}
          </Button>
        )}
        <Button variant="ghost" size="sm" disabled={anyBusy} onClick={onDelete}
          title={t('stock.import.remove.title', { defaultValue: 'Delete this import?' })}>
          <Trash2 className="h-4 w-4 text-danger" aria-hidden />
        </Button>
      </div>
    </li>
  );
}

/** Before → after for every field this row would move. */
function RowDiff({ row }: { row: ImportRow }): JSX.Element {
  const { t } = useTranslation('inventory');
  const b = row.before;
  const parts: Array<{ label: string; from: string | null; to: string }> = [];
  const add = (label: string, from: string | null | undefined, to: string | null): void => {
    if (to === null || to === '') return;
    const same = from !== null && from !== undefined && from !== '' &&
      (Number.isNaN(Number(from)) || Number.isNaN(Number(to))
        ? from === to
        : Number(from) === Number(to));
    if (same) return;
    parts.push({ label, from: from ?? null, to });
  };

  if (row.action === 'create') {
    return (
      <span className="text-xs text-fg-muted">
        {row.name} · {row.unit ?? '—'} ·{' '}
        {t('stock.columns.quantity')} {row.quantity ? trimMoney(row.quantity) : '0'}
      </span>
    );
  }
  if (row.action !== 'update') {
    return <span className="text-xs text-fg-subtle">{row.name}</span>;
  }

  add(t('stock.import.field.name', { defaultValue: 'Name' }), b?.name, row.name || null);
  add(t('stock.import.field.unit', { defaultValue: 'Unit' }), b?.unit, row.unit);
  add(t('stock.import.field.retail', { defaultValue: 'Retail' }), b?.retailPrice, row.retailPrice);
  add(t('stock.import.field.market', { defaultValue: 'Market' }), b?.marketPrice, row.marketPrice);
  add(t('stock.import.field.stationery', { defaultValue: 'Stationery' }), b?.stationeryPrice, row.stationeryPrice);
  add(t('stock.import.field.qty', { defaultValue: 'On hand' }), b?.quantity ?? '0', row.quantity);
  add(t('stock.import.field.cost', { defaultValue: 'Cost' }), b?.unitCost, row.unitCost);

  if (parts.length === 0) {
    return <span className="text-xs text-fg-subtle">{t('stock.import.noChange', { defaultValue: 'No change' })}</span>;
  }
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
      {parts.map((p) => (
        <span key={p.label} className="whitespace-nowrap">
          <span className="text-fg-muted">{p.label}: </span>
          <span className="text-fg-subtle line-through">{p.from === null ? '—' : maybeTrim(p.from)}</span>
          <span className="mx-1 text-fg-muted">→</span>
          <span className="font-semibold">{maybeTrim(p.to)}</span>
        </span>
      ))}
    </div>
  );
}

/** Drop trailing decimal zeros on numbers, leave text alone. */
function maybeTrim(v: string): string {
  return Number.isNaN(Number(v)) || v.trim() === '' ? v : trimMoney(v);
}
